import React, { useState, useEffect } from 'react';
import { Brain, Lightbulb, Target, MessageSquare, BookOpen, Zap, CheckCircle, Heart, Share2, Copy, RefreshCw, ChevronDown, ChevronUp, X, Search, Trash2, Quote } from 'lucide-react';

interface ArticleInsight {
  id: string;
  type: 'summary' | 'key-points' | 'questions' | 'quotes' | 'concepts' | 'action-items';
  title: string;
  content: string;
  confidence: number;
  relevance: number;
  createdAt: Date;
  isGenerated: boolean;
  isVerified: boolean;
  likes: number;
  isLiked: boolean;
  tags: string[];
  source: string;
  position?: {
    startOffset: number;
    endOffset: number;
    xpath: string;
  };
}

interface AIArticleInsightsProps {
  articleId: string;
  articleContent: string;
  articleTitle: string;
  onInsightCreate?: (insight: ArticleInsight) => void;
  onInsightUpdate?: (insight: ArticleInsight) => void;
  onInsightDelete?: (insightId: string) => void;
  onInsightShare?: (insight: ArticleInsight) => void;
  className?: string;
}

const AIArticleInsights: React.FC<AIArticleInsightsProps> = ({
  articleId,
  articleContent,
  articleTitle,
  onInsightCreate,
  onInsightUpdate,
  onInsightDelete,
  onInsightShare,
  className = ''
}) => {
  const [insights, setInsights] = useState<ArticleInsight[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showInsights, setShowInsights] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'confidence' | 'relevance' | 'date'>('confidence');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Mock AI-generated insights
  useEffect(() => {
    const mockInsights: ArticleInsight[] = [
      {
        id: '1',
        type: 'summary',
        title: 'Article Summary',
        content: 'This article explores the future of web development, covering React Server Components, WebAssembly, AI-powered tools, and edge computing. The author provides a comprehensive overview of emerging technologies that will shape how developers build for the web in 2024 and beyond.',
        confidence: 0.95,
        relevance: 0.92,
        createdAt: new Date(),
        isGenerated: true,
        isVerified: false,
        likes: 12,
        isLiked: false,
        tags: ['summary', 'overview', 'web-development'],
        source: 'AI Analysis'
      },
      {
        id: '2',
        type: 'key-points',
        title: 'Key Points',
        content: '• React Server Components reduce bundle size and improve performance\n• WebAssembly enables native performance in browsers\n• AI-powered development tools are becoming mainstream\n• Edge computing is reshaping web architecture\n• Server-side rendering is making a comeback',
        confidence: 0.88,
        relevance: 0.94,
        createdAt: new Date(),
        isGenerated: true,
        isVerified: false,
        likes: 8,
        isLiked: true,
        tags: ['key-points', 'bullets', 'web-development'],
        source: 'AI Analysis'
      },
      {
        id: '3',
        type: 'questions',
        title: 'Discussion Questions',
        content: '1. How will React Server Components change the way we think about client-side state management?\n2. What are the security implications of running WebAssembly in browsers?\n3. How can developers prepare for the shift to edge computing?\n4. Will AI tools replace human developers or augment their capabilities?',
        confidence: 0.82,
        relevance: 0.89,
        createdAt: new Date(),
        isGenerated: true,
        isVerified: false,
        likes: 5,
        isLiked: false,
        tags: ['questions', 'discussion', 'critical-thinking'],
        source: 'AI Analysis'
      },
      {
        id: '4',
        type: 'quotes',
        title: 'Notable Quotes',
        content: '"React Server Components represent one of the most significant shifts in how we think about React applications."\n\n"WebAssembly is revolutionizing web performance by allowing developers to run code written in languages like C, C++, and Rust directly in the browser."',
        confidence: 0.91,
        relevance: 0.87,
        createdAt: new Date(),
        isGenerated: true,
        isVerified: false,
        likes: 15,
        isLiked: true,
        tags: ['quotes', 'highlights', 'important'],
        source: 'AI Analysis'
      },
      {
        id: '5',
        type: 'concepts',
        title: 'Key Concepts',
        content: 'React Server Components, WebAssembly (WASM), Edge Computing, AI-Powered Development, Server-Side Rendering, Bundle Size Optimization, Performance Metrics, Core Web Vitals, Progressive Web Apps, Microservices Architecture',
        confidence: 0.86,
        relevance: 0.93,
        createdAt: new Date(),
        isGenerated: true,
        isVerified: false,
        likes: 7,
        isLiked: false,
        tags: ['concepts', 'terminology', 'definitions'],
        source: 'AI Analysis'
      },
      {
        id: '6',
        type: 'action-items',
        title: 'Action Items',
        content: '1. Learn React Server Components and experiment with them in a new project\n2. Explore WebAssembly for performance-critical applications\n3. Set up monitoring for Core Web Vitals in existing projects\n4. Research edge computing platforms and their benefits\n5. Evaluate AI-powered development tools for your workflow',
        confidence: 0.79,
        relevance: 0.91,
        createdAt: new Date(),
        isGenerated: true,
        isVerified: false,
        likes: 9,
        isLiked: false,
        tags: ['action-items', 'todo', 'next-steps'],
        source: 'AI Analysis'
      }
    ];

    setInsights(mockInsights);
  }, [articleId]);

  const insightTypes = [
    { id: 'all', name: 'All Insights', icon: Brain, color: '#6366f1' },
    { id: 'summary', name: 'Summary', icon: BookOpen, color: '#3b82f6' },
    { id: 'key-points', name: 'Key Points', icon: Target, color: '#10b981' },
    { id: 'questions', name: 'Questions', icon: MessageSquare, color: '#f59e0b' },
    { id: 'quotes', name: 'Quotes', icon: Quote, color: '#8b5cf6' },
    { id: 'concepts', name: 'Concepts', icon: Lightbulb, color: '#06b6d4' },
    { id: 'action-items', name: 'Action Items', icon: CheckCircle, color: '#ef4444' }
  ];

  const generateInsights = async () => {
    setIsGenerating(true);
    
    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Add new insights
    const newInsights: ArticleInsight[] = [
      {
        id: Date.now().toString(),
        type: 'summary',
        title: 'Updated Summary',
        content: 'This is a newly generated summary based on the latest AI analysis...',
        confidence: 0.92,
        relevance: 0.89,
        createdAt: new Date(),
        isGenerated: true,
        isVerified: false,
        likes: 0,
        isLiked: false,
        tags: ['ai-generated', 'updated'],
        source: 'AI Analysis'
      }
    ];
    
    setInsights(prev => [...prev, ...newInsights]);
    setIsGenerating(false);
  };

  const updateInsight = (insightId: string, updates: Partial<ArticleInsight>) => {
    setInsights(prev => prev.map(insight => 
      insight.id === insightId 
        ? { ...insight, ...updates }
        : insight
    ));
    
    const updatedInsight = insights.find(i => i.id === insightId);
    if (updatedInsight) {
      onInsightUpdate?.({ ...updatedInsight, ...updates });
    }
  };

  const deleteInsight = (insightId: string) => {
    setInsights(prev => prev.filter(i => i.id !== insightId));
    onInsightDelete?.(insightId);
  };

  const shareInsight = (insight: ArticleInsight) => {
    onInsightShare?.(insight);
  };

  const filteredInsights = insights
    .filter(insight => {
      const matchesType = selectedType === 'all' || insight.type === selectedType;
      const matchesSearch = searchQuery === '' || 
        insight.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        insight.content.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'confidence':
          return b.confidence - a.confidence;
        case 'relevance':
          return b.relevance - a.relevance;
        case 'date':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default:
          return 0;
      }
    });

  const getInsightIcon = (type: string) => {
    const insightType = insightTypes.find(t => t.id === type);
    return insightType?.icon || Brain;
  };

  const getInsightColor = (type: string) => {
    const insightType = insightTypes.find(t => t.id === type);
    return insightType?.color || '#6366f1';
  };

  return (
    <div className={`ai-article-insights ${className}`}>
      {/* Insights Panel */}
      {showInsights && (
        <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                AI Insights
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setShowInsights(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Generate Button */}
            <button
              onClick={generateInsights}
              disabled={isGenerating}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors mb-4"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  Generate New Insights
                </>
              )}
            </button>

            {/* Filters */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search insights..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  {insightTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="confidence">Confidence</option>
                  <option value="relevance">Relevance</option>
                  <option value="date">Date</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {filteredInsights.map(insight => {
                const Icon = getInsightIcon(insight.type);
                const color = getInsightColor(insight.type);
                
                return (
                  <div
                    key={insight.id}
                    className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" 
                        style={{ backgroundColor: color + '20' }}
                      >
                        <Icon className="w-4 h-4" style={{ color }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                            {insight.title}
                          </h4>
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            {insight.type}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 leading-relaxed">
                          {insight.content}
                        </p>
                        
                        {/* Confidence and Relevance */}
                        <div className="flex items-center gap-4 mb-3">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">Confidence:</span>
                            <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                              <div 
                                className="h-full bg-green-500 rounded-full transition-all duration-300"
                                style={{ width: `${insight.confidence * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">
                              {Math.round(insight.confidence * 100)}%
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">Relevance:</span>
                            <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                              <div 
                                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                                style={{ width: `${insight.relevance * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">
                              {Math.round(insight.relevance * 100)}%
                            </span>
                          </div>
                        </div>
                        
                        {/* Tags */}
                        {insight.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {insight.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateInsight(insight.id, { isLiked: !insight.isLiked, likes: insight.isLiked ? insight.likes - 1 : insight.likes + 1 })}
                            className={`p-1 rounded transition-colors ${
                              insight.isLiked 
                                ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' 
                                : 'text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${insight.isLiked ? 'fill-current' : ''}`} />
                          </button>
                          
                          <span className="text-xs text-gray-500">{insight.likes}</span>
                          
                          <button
                            onClick={() => shareInsight(insight)}
                            className="p-1 text-gray-400 hover:text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => navigator.clipboard.writeText(insight.content)}
                            className="p-1 text-gray-400 hover:text-green-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => deleteInsight(insight.id)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredInsights.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Brain className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No insights found</p>
                  <p className="text-sm">Generate AI insights to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIArticleInsights;
